import React, { useState, useEffect } from "react";
import {
  createStyles,
  makeStyles,
  Theme,
  Grid,
  Typography
} from "@material-ui/core";
import { useDispatch } from "react-redux";
import { toast } from "../../app";
import {
  CheckboxWithLabel,
  DropdownWithLabel,
  FolderWithLabel,
  SimpleDialog,
  memoWithOpen
} from "../../components";
import { fs, getTemporaryFile } from "../../electron-modules";
import { deleteAsset } from "../../redux/assets-slice";
import { exportCamerasToCSV } from "../../folder";
import { addAssetFolder } from "../../utilities";
import path from "path";
import { useTaskQueue, useViewer } from "../../hooks";
import { useTranslation } from "react-i18next";

enum DetectionType {
  VehiclesAndPeople="vehicles_and_people",
  Vehicles="vehicles",
  People="people"
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mixedDataWarning: {
      padding: theme.spacing(1.0),
      color: theme.palette.warning.main,
      textAlign: "center"
    }
  }),
);

export const BlurVehiclesPeople = memoWithOpen((props) => {
  const {assets, open, onClose} = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const {viewer} = useViewer();
  const {addRunningTask} = useTaskQueue();
  const {t} = useTranslation();

  const [outputPath, setOutputPath] = useState(null);
  const [detectionType, setDetectionType] = useState(DetectionType.VehiclesAndPeople);
  const [blurStrength, setBlurStrength] = useState(10);
  const [accuracy, setAccuracy] = useState(0.5);
  const [importResult, setImportResult] = useState(true);

  // TODO: actually handle multiple inputs (sent multiple exe commands to processing queue)
  const asset = assets ? assets[0] : undefined;

  const resetDefaults = () => {
    setOutputPath(null);
  };

  const handleSubmit = () => {
    const pythonName = "blur_vehicles_people";

    const tempImagePath = getTemporaryFile("json");
    const imagesCSVPath = path.join(outputPath, "blurred-images.csv");

    const cameras = viewer.getCamerasForCSV(asset.id);
    const cameraData = cameras.map(x => x.path);
    const pathsJSON = JSON.stringify(cameraData, null, 4);

    try {
      fs.writeFileSync(tempImagePath, pathsJSON, 'utf8');
    } catch(err) {
      console.log(err);
      toast.error(t('functions.error_running_function'));
      return;
    }

    let commands = [
      "-p", pythonName,
      "--input_path", tempImagePath,
      "--output_path", outputPath,
      "--blur_options", detectionType,
      "--blur_level", blurStrength,
      "--detection-accuracy", accuracy
    ];

    const onFinish = async () => {
      if (!importResult) return;

      // Generate camera csv in output folder
      const cameras = viewer.getCamerasForCSV(asset.id);
      exportCamerasToCSV(cameras, imagesCSVPath);

      // Remove old camera csv
      dispatch(deleteAsset(asset.id));

      // Add new camera csv
      await addAssetFolder(dispatch, [imagesCSVPath], asset.folderID);
    };

    onClose();
    addRunningTask(commands, onFinish);
  };

  useEffect(() => {
    if (open) return;
    resetDefaults();
  }, [open]);

  const cameras = asset ? viewer.getCamerasForCSV(asset.id) : [];
  const folders = viewer.getCameraFolders(cameras);
  const inputFolders = new Set([...folders]);
  const pathOverlap = inputFolders.has(outputPath);
  const canSubmit = !pathOverlap && (outputPath !== null);

  return (
    <React.Fragment>
      <SimpleDialog
        open={open}
        onClose={onClose}
        onSubmit={handleSubmit}
        canSubmit={canSubmit}
        title={t('functions.blur_vehicles_people.title')}
        button={t('buttons.submit')}
      >
        <Grid container spacing={2}>

          {pathOverlap && (<Grid item xs={12} className={classes.mixedDataWarning}>
            <Typography variant="caption" align="center">
              {t('point-markup.output-folder-must-be-different')}
            </Typography>
          </Grid>)}

          {/** Detection Type */}
          <Grid item xs={6}>
            <DropdownWithLabel
              title={t('functions.blur_vehicles_people.detection_type')}
              label={t('functions.blur_vehicles_people.the_types_of_objects')}
              value={detectionType}
              onChange={setDetectionType}
              options={[
                [DetectionType.VehiclesAndPeople, t('functions.blur_vehicles_people.vehicles_and_people')],
                [DetectionType.Vehicles, t('functions.blur_vehicles_people.vehicles')],
                [DetectionType.People, t('functions.blur_vehicles_people.people')],
              ]}
            />
          </Grid>

          {/** Detection Accuracy */}
          <Grid item xs={6}>
            <DropdownWithLabel
              title={t('functions.blur_vehicles_people.detection_accuracy')}
              label={t('functions.blur_vehicles_people.the_detection_accuracy_used')}
              value={accuracy}
              onChange={setAccuracy}
              options={
                [...Array(10).keys()].map(index => {
                  const value = (index+1)/10;
                  const text = `${(index+1)*10}%`;
                  return [value, text];
                })
              }
            />
          </Grid>

          {/** Blur Level */}
          <Grid item xs={6}>
            <DropdownWithLabel
              title={t('functions.blur_vehicles_people.blurring_level')}
              label={t('functions.blur_vehicles_people.the_strength_of_the_guassian')}
              value={blurStrength}
              onChange={setBlurStrength}
              options={
                [...Array(20).keys()].map(index => {
                  const value = index+1;
                  const text = `${index+1}`;
                  return [value, text];
                })
              }
            />
          </Grid>

          {/** Import results */}
          <Grid item xs={6}>
            <CheckboxWithLabel
              title={t('functions.import_results')}
              label={t('functions.blur_vehicles_people.blurred_images_will_be_imported')}
              value={importResult}
              onChange={setImportResult}
            />
          </Grid>

          {/** Output folder */}
          <Grid item xs={6}>
            <FolderWithLabel
              title={t('point-markup.output-folder')}
              label={t('functions.blur_vehicles_people.output_folder_for_blurred_images')}
              value={outputPath}
              onChange={setOutputPath}
            />
          </Grid>

        </Grid>
      </SimpleDialog>
    </React.Fragment>
  );
});
